import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNotNil} from 'ramda-adjunct';

import {
  useGetAutotracerPlConfigQuery,
  useGetCarVerticalConfigQuery,
  useGetCebiaAutotracerAndReportConfigQuery,
  useGetCebiaRokvyConfigQuery,
  useGetParticipationQuery,
} from '@dms/api/core';
import {
  useBulkCreateHighlightsMutation,
  useBulkCreateShortcomingsMutation,
  useDeleteHighlightMutation,
  useDeleteShortcomingMutation,
  useGetVehicleQuery,
  useListHighlightsForTenantQuery,
  useListHighlightsQuery,
  useListShortcomingsForTenantQuery,
  useListShortcomingsQuery,
} from '@dms/api/sales';
import {useGetCarPassConfigQuery} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import {selectSaleVehicle, useGetCebiaReportsQuery} from '@dms/api/vehicle';
import {useGetServiceVehicleQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  CardTagSelection,
  CardTagSelectionTagType,
  CommentsWidget,
  handleApiError,
  PriceReportWidget,
  TagType,
  useIsLicenceEnabled,
  usePermissions,
  useTenant,
  VehicleNoteWidget,
} from '@dms/shared';
import {CebiaAutotracerWidget, CebiaProductionYearWidget, CebiaReportWidget} from '@dms/teas';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {CarPassWidget} from '../CarPassWidget/CarPassWidget';
import {CarVerticalWidget} from '../CarVerticalWidget/CarVerticalWidget';
import {MiraPredictionWidget} from '../MiraPredictionWidget/MiraPredictionWidget';
import {PkwEnvkvCard} from '../PkwEnvkvCard/PkwEnvkvCard';
import {VehicleHistoryReportWidget} from '../VehicleHistoryReportWidget/VehicleHistoryReportWidget';
import {VehicleFinancialInstitution} from './components/VehicleFinancialInstitution';
import {VehicleInsurance} from './components/VehicleInsurance';
import {
  getIntegrationSectionVariant,
  getIsIntegrationSectionEmpty,
} from './utils/getIntegrationSectionState';

const remapLabelToText = ({id, title}: TagType): CardTagSelectionTagType => ({id, label: title});

interface DetailsSideColumnProps extends TestIdProps {
  isIntegrationsShown?: boolean;
  isInsuranceShown?: boolean;
  isDetailsTab?: boolean;
  vehicleId: string;
}

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function DetailsSideColumn(props: DetailsSideColumnProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: vehicleHighlights} = useListHighlightsQuery({vehicleId: props.vehicleId});
  const {data: vehicleShortcoming} = useListShortcomingsQuery({vehicleId: props.vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const navigate = useNavigate();
  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
    hasVehicleEnableLeasingAndCreditCompany,
    hasVehicleDisableLeasingAndCreditCompany,
    hasVehicleSetLeasingAndCreditCompany,
    hasVehicleSetFleetInsurance,
    hasVehicleUpdateStorageSpace,
    canEditHighlights,
    canEditShortcomings,
    canReadBusinessCase,
    canReadPriceReportWidget,
    canReadAdvertisingSettings,
    canReadAutotracerPl,
    canGeneratePkwEnvkvDocument,
  ] = usePermissions({
    permissionKeys: [
      'vehicleCommentRead',
      'vehicleCommentAdd',
      'vehicleCommentDelete',
      'vehicleEnableLeasingAndCreditCompany',
      'vehicleDisableLeasingAndCreditCompany',
      'vehicleSetLeasingAndCreditCompany',
      'vehicleSetFleetInsurance',
      'vehicleUpdateStorageSpace',
      'editHighlights',
      'editShortcomings',
      'businessCaseRead',
      'createVehiclePriceReport',
      'readAdvertisingSettings',
      'readAutotracerPl',
      'generatePkwEnvkvDocument',
    ],
    scopes: {
      vehicleCommentRead: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleCommentAdd: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleEnableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleDisableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleSetLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleSetFleetInsurance: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleUpdateStorageSpace: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      editHighlights: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      editShortcomings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleCommentDelete: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      generatePkwEnvkvDocument: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const canEditSaleVehicle =
    hasVehicleEnableLeasingAndCreditCompany &&
    hasVehicleDisableLeasingAndCreditCompany &&
    hasVehicleSetLeasingAndCreditCompany &&
    hasVehicleSetFleetInsurance &&
    hasVehicleUpdateStorageSpace;

  const [createShortcomings] = useBulkCreateShortcomingsMutation();
  const [deleteShortcoming] = useDeleteShortcomingMutation();
  const [createHighlights] = useBulkCreateHighlightsMutation();
  const [deleteHighlight] = useDeleteHighlightMutation();
  const {data: highlights} = useListHighlightsForTenantQuery({size: 1000});
  const {data: shortcomings} = useListShortcomingsForTenantQuery({size: 1000});
  const {data: cebiaReports} = useGetCebiaReportsQuery(
    isNotNil(props.vehicleId) ? {vehicleId: props.vehicleId} : skipToken
  );

  const {data: cebiaAutotracerAndReportConfig, isLoading: isCebiaAutotracerAndReportConfigLoading} =
    useGetCebiaAutotracerAndReportConfigQuery();
  const {data: cebiaRokvyConfig, isLoading: isCebiaRokvyConfigLoading} =
    useGetCebiaRokvyConfigQuery();
  const {data: carVerticalConfig, isLoading: isCarVerticalConfigLoading} =
    useGetCarVerticalConfigQuery();
  const {data: autotracerPlConfig, isLoading: isAutotracerPlConfigLoading} =
    useGetAutotracerPlConfigQuery(canReadAutotracerPl ? undefined : skipToken);

  const {isCarPassEnabled, isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const {isTenantCountryRestricted} = useTenant();
  const isBelgianTenant = isTenantCountryRestricted(['BEL']);
  const {data: carPassConfig, isLoading: isCarPassConfigLoading} = useGetCarPassConfigQuery(
    isBelgianTenant && isNotNil(vehicle?.branchId) ? {branchId: vehicle.branchId} : skipToken
  );

  const {data: saleVehicle} = useSelector(selectSaleVehicle(props.vehicleId));
  const {data: serviceVehicle} = useGetServiceVehicleQuery(
    isWorkshopModuleEnabled ? {vehicleId: props.vehicleId} : skipToken
  );
  const isSaleVehicle = isNotNil(saleVehicle);
  const isServiceVehicle = isNotNil(serviceVehicle);

  const handleAddHighlights = (values: CardTagSelectionTagType[]) =>
    createHighlights({
      vehicleId: props.vehicleId,
      highlightBulkRequestBody: {highlights: values.map(({label}) => ({text: label}))},
    })
      .unwrap()
      .catch(handleApiError);

  const handleAddShortcomings = (values: CardTagSelectionTagType[]) =>
    createShortcomings({
      vehicleId: props.vehicleId,
      shortcomingBulkRequestBody: {shortcomings: values.map(({label}) => ({text: label}))},
    })
      .unwrap()
      .catch(handleApiError);

  const handleDeleteHighlight = (value: CardTagSelectionTagType) =>
    deleteHighlight({vehicleId: props.vehicleId, highlightId: value.id})
      .unwrap()
      .catch(handleApiError);

  const handleDeleteHighlightBulk = (values: CardTagSelectionTagType[]) =>
    Promise.all(
      values.map((value) =>
        deleteHighlight({vehicleId: props.vehicleId, highlightId: value.id}).unwrap()
      )
    ).catch(handleApiError);

  const handleDeleteShortcoming = (value: CardTagSelectionTagType) =>
    deleteShortcoming({vehicleId: props.vehicleId, shortcomingId: value.id})
      .unwrap()
      .catch(handleApiError);

  const handleDeleteShortcomingBulk = (values: CardTagSelectionTagType[]) =>
    Promise.all(
      values.map((value) =>
        deleteShortcoming({vehicleId: props.vehicleId, shortcomingId: value.id}).unwrap()
      )
    ).catch(handleApiError);

  const canShowCarPassWidget =
    isNotNil(vehicle) && isCarPassEnabled && isBelgianTenant && (carPassConfig?.enabled ?? false);
  const canShowCarVerticalWidget =
    isNotNil(vehicle) &&
    isFeatureEnabled(featureFlags.CAR_VERTICAL_INTEGRATION) &&
    (carVerticalConfig?.enabled ?? false);
  const canShowCebiaAutotracerWidget =
    isNotNil(vehicle) && (cebiaAutotracerAndReportConfig?.enabled ?? false);
  const canShowCebiaReportWidget = isNotNil(vehicle) && (cebiaReports?.length ?? 0) > 0;
  const canShowCebiaRokvyWidget = isNotNil(vehicle) && (cebiaRokvyConfig?.enabled ?? false);
  const canShowVehicleHistoryReportWidget =
    isNotNil(vehicle) && canReadAutotracerPl && (autotracerPlConfig?.enabled ?? false);
  const canShowMiraPredictionWidget =
    isNotNil(vehicle) &&
    isNotNil(vehicle.make) &&
    isNotNil(vehicle.modelFamily) &&
    isFeatureEnabled(featureFlags.MIRA_PREDICTIVE_MAINTENANCE);
  const integrationSectionVariant = getIntegrationSectionVariant({
    isIntegrationsShown: props.isIntegrationsShown,
    isSaleVehicle,
    isServiceVehicle,
    canReadBusinessCase,
    isWorkshopModuleEnabled,
  });

  const isWidgetEmpty = getIsIntegrationSectionEmpty({
    variant: integrationSectionVariant,
    canReadPriceReportWidget,
    canShowCarPassWidget,
    canShowCarVerticalWidget,
    canShowCebiaAutotracerWidget,
    canShowCebiaReportWidget,
    canShowCebiaRokvyWidget,
    canShowVehicleHistoryReportWidget,
    canShowMiraPredictionWidget,
  });
  const isWidgetLoading =
    isCebiaAutotracerAndReportConfigLoading ||
    isCebiaRokvyConfigLoading ||
    isCarVerticalConfigLoading ||
    isCarPassConfigLoading ||
    isAutotracerPlConfigLoading;

  return (
    <>
      <PkwEnvkvCard
        vehicleId={props.vehicleId}
        canGenerateDocument={canGeneratePkwEnvkvDocument}
        data-testid={testIds.vehicles.detail('pkwEnvkvCard')}
      />
      <Show
        when={
          integrationSectionVariant !== 'hidden' &&
          props.isIntegrationsShown &&
          props.vehicleId !== ''
        }
      >
        <Card
          title={i18n.t('entity.vehicle.labels.integrations')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              leftIcon: 'action/settings',
              onClick: () => navigate(settingsRoutes.vehicleServices),
              'data-testid': testIds.vehicles.detail('integrationSettingsButton'),
              title: i18n.t('general.labels.settings'),
            },
          ]}
          isExpandable
        >
          <VStack spacing={3}>
            <DataStatus
              isEmpty={isWidgetEmpty}
              isLoading={isWidgetLoading}
              emptyMessage={i18n.t('entity.vehicle.emptyIntegration.noServicesAvailable')}
              emptySubheadline={i18n.t('entity.vehicle.emptyIntegration.enableInSettings')}
            >
              <Show when={canShowCarPassWidget}>
                <CarPassWidget
                  vehicleId={props.vehicleId}
                  data-testid={testIds.vehicles.detail('carPassWidget')}
                />
              </Show>
              <Show when={integrationSectionVariant === 'sale' && canReadPriceReportWidget}>
                <PriceReportWidget
                  resourceId={props.vehicleId}
                  vehicleType={vehicle?.type}
                  context="vehicle"
                  data-testid={testIds.vehicles.detail('priceReportWidget')}
                />
              </Show>
              <Show when={canShowCebiaAutotracerWidget}>
                <CebiaAutotracerWidget
                  vehicle={vehicle!}
                  isSmartCodeUrlPublishSwitchShown={integrationSectionVariant === 'sale'}
                  data-testid={testIds.vehicles.detail('cebiaAutotracerWidget')}
                />
              </Show>
              <Show when={canShowVehicleHistoryReportWidget}>
                <VehicleHistoryReportWidget
                  vehicleId={props.vehicleId}
                  vehicle={vehicle}
                  data-testid={testIds.vehicles.detail('vehicleHistoryReportWidget')}
                />
              </Show>
              <Show when={integrationSectionVariant === 'sale' && canShowCebiaReportWidget}>
                <CebiaReportWidget
                  vehicleId={vehicle?.id}
                  data-testid={testIds.vehicles.detail('cebiaReportWidget')}
                />
              </Show>
              <Show when={integrationSectionVariant === 'sale' && canShowCebiaRokvyWidget}>
                <CebiaProductionYearWidget
                  data-testid={testIds.vehicles.detail('cebiaProductionYearWidget')}
                  vehicle={vehicle!}
                />
              </Show>
              <Show when={integrationSectionVariant === 'sale' && canShowCarVerticalWidget}>
                <CarVerticalWidget
                  vehicleId={vehicle?.id}
                  data-testid={testIds.vehicles.detail('carVericalWidget')}
                />
              </Show>
              <Show when={canShowMiraPredictionWidget}>
                <MiraPredictionWidget
                  vehicleId={props.vehicleId}
                  data-testid={testIds.vehicles.detail('miraPredictionWidget')}
                />
              </Show>
            </DataStatus>
          </VStack>
        </Card>
      </Show>

      <Show when={hasVehicleCommentReadPermission}>
        <CommentsWidget
          title={i18n.t('entity.vehicle.labels.vehicleComments')}
          resourceId={EntityResourceIds.vehicle}
          recordId={props.vehicleId}
          isAddDisabled={!hasVehicleCommentAddPermission}
          isDeleteDisabled={!hasVehicleCommentDeletePermission}
          data-testid={testIds.vehicles.detail('vehicleComments')}
        />
      </Show>
      <VehicleNoteWidget
        vehicleId={props.vehicleId}
        title={i18n.t('entity.vehicle.labels.vehicleAdditionalNotes')}
      />
      <Show when={isSaleVehicle}>
        <VehicleFinancialInstitution
          canEditVehicle={canEditSaleVehicle}
          vehicleId={props.vehicleId}
          data-testid={suffixTestId('financing', props)}
        />
      </Show>
      <Show when={!isSaleVehicle && !props.isDetailsTab && isWorkshopModuleEnabled}>
        <VehicleFinancialInstitution
          vehicleId={props.vehicleId}
          canEditVehicle={canEditSaleVehicle}
          data-testid={suffixTestId('financing', props)}
        />
      </Show>
      <Show when={props.isInsuranceShown}>
        <VehicleInsurance
          data-testid="detailsSideColumn-vehicleInsurance"
          canEditVehicle={canEditSaleVehicle}
          vehicleId={props.vehicleId}
        />
      </Show>
      <Show
        when={
          isSaleVehicle &&
          canReadAdvertisingSettings &&
          saleVehicle?.vehicleId &&
          canReadBusinessCase
        }
      >
        <CardTagSelection
          tags={vehicleHighlights?.map(remapLabelToText) ?? []}
          predefinedTags={highlights?.map(remapLabelToText) || []}
          onDelete={handleDeleteHighlight}
          onCreateBulk={handleAddHighlights}
          onDeleteBulk={handleDeleteHighlightBulk}
          isDisabled={!canEditHighlights}
          data-testid={testIds.vehicles.detail('highlights')}
          title={i18n.t('entity.vehicle.labels.carHighlights')}
          editDialogTitle={i18n.t('entity.vehicle.labels.carHighlights')}
          emptyMessage={i18n.t('entity.vehicle.labels.highlightedFeaturesPlaceholder')}
          tagsColorScheme="neutral"
        />

        <CardTagSelection
          tags={vehicleShortcoming?.map(remapLabelToText) ?? []}
          predefinedTags={shortcomings?.map(remapLabelToText) || []}
          onDelete={handleDeleteShortcoming}
          onCreateBulk={handleAddShortcomings}
          onDeleteBulk={handleDeleteShortcomingBulk}
          isDisabled={!canEditShortcomings}
          data-testid={testIds.vehicles.detail('shortcomings')}
          title={i18n.t('entity.vehicle.labels.shortcomings')}
          editDialogTitle={i18n.t('entity.vehicle.labels.shortcomings')}
          emptyMessage={i18n.t('entity.vehicle.labels.shortcomingsPlaceholder')}
          tagsColorScheme="red"
        />
      </Show>
    </>
  );
}
